import { useCallback, useLayoutEffect, useRef, useState } from "react";
import "./index.css";
/**
 * @callback onClickHandler
 * @param {PointerEvent} e
 * @param {Object} context
 * @typedef {("opacity" | "overlay")} Varaint
 */

/**
 * @param {Object} props
 * @param {onClickHandler} props.onClickHandler - What to do when click on button
 * @param {boolean} props.isDisabled
 * @param {boolean} props.isSelected
 * @param {boolean} props.isInActiveVaraint
 * @param {Varaint} props.varaint - One of "opacity" or "overlay"
 * @param {string} props.cardBgColor - background color for card eg 'primary-card' note not in var
 * @param {string} props.isNoHoverRequired - if no hover is required pass true else by default is false
 * @param {string} props.isNoCursorRequired - if no cursor is required pass true else by default is false
 * @param {string} props.minHeight - optional to pass min height of card
 * @param {string} [props.maxWidth] - optional to pass max width of card
 * @param {string} props.hoverHeight - pass hover part height for proper animation
 * @param {React.ReactNode} props.children
 * @param {Object} props.context
 * @returns {JSX.Element}
 */
const CardContainer = ({
  isDisabled,
  isSelected,
  onClickHandler,
  children,
  context,
  cardBgColor,
  isNoHoverRequired,
  isNoCursorRequired,
  minHeight,
  maxWidth,
  filterRef,
  varaint = "opacity",
  isInActiveVaraint = false,
  hoverHeight = 0,
}) => {
  const cardRef = useRef(null);
  const hoverRef = useRef(null);
  const [cardHeight, setCardHeight] = useState("fit-content");
  const [openUpward, setOpenUpward] = useState(false);
  const [measuredHoverHeight, setMeasuredHoverHeight] = useState(0);

  let cardBody, cardHover;

  if (!Array.isArray(children)) {
    cardBody = children;
    cardHover = null;
  } else if (children.length > 2) {
    throw new Error(
      "Card container cannot have any more than a <card-body> and a <card-hover>",
    );
  } else {
    [cardBody, cardHover] = children;
  }

  const measure = useCallback(() => {
    if (cardRef.current) {
      const h = cardRef.current.getBoundingClientRect().height;
      if (h > 0) setCardHeight(`${h}px`);
    }
    if (hoverRef.current) {
      const h = hoverRef.current.scrollHeight;
      if (h > 0) setMeasuredHoverHeight(h);
    }
  }, []);

  useLayoutEffect(() => {
    measure();

    const observer = new ResizeObserver(() => measure());
    if (cardRef.current) observer.observe(cardRef.current);
    if (hoverRef.current) observer.observe(hoverRef.current);

    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const computeDirection = () => {
    const card = cardRef.current;
    if (!card) return;

    const container = filterRef?.current;
    const cardRect = card.getBoundingClientRect();
    const actualHoverHeight = measuredHoverHeight || Number(hoverHeight);

    const containerRect = container
      ? container.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight };

    const paginationHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--pagination-height",
        ),
      ) || 0;

    const containerTop = containerRect.top;
    const containerBottom = containerRect.bottom - paginationHeight;

    const spaceBelow = containerBottom - cardRect.bottom;
    const spaceAbove = cardRect.top - containerTop;

    const willOverflowBottom =
      spaceBelow < actualHoverHeight && spaceAbove > actualHoverHeight;

    setOpenUpward(willOverflowBottom);
  };

  const handleMouseEnter = () => {
    computeDirection();
  };

  const handleTouchStart = () => {
    computeDirection();
  };

  return (
    <div
      ref={cardRef}
      style={{ height: minHeight || cardHeight, maxWidth: maxWidth || "" }}
      className="standard-card-parent-container"
      onMouseEnter={handleMouseEnter}
      onTouchStart={handleTouchStart}
    >
      <div
        style={{
          top: openUpward ? undefined : 0,
          bottom: openUpward ? 0 : undefined,
        }}
        onClick={(e) => {
          if (!isDisabled || isInActiveVaraint) onClickHandler(e, context);
        }}
        className={` ${isNoHoverRequired ? "" : isDisabled ? "" : isSelected ? "global-hover-black" : "global-hover-white"}  ${isDisabled ? `cursor-not-allowed ${varaint === "opacity" ? "standard-card-container-disabled-opacity" : "standard-card-container-disabled-overlay"} ` : isNoCursorRequired ? "standard-card-container cursor-default" : "cursor-pointer standard-card-container"} ${isSelected ? "bg-positive-bg" : cardBgColor ? `bg-${cardBgColor}` : "bg-primary-card"}`}
      >
        <div
          className={`${cardHover ? "standard-card-div" : "standard-card-div-inactive"}`}
        >
          {cardBody}
        </div>
        <div
          ref={hoverRef}
          className={cardHover ? "standard-card-hover-div" : ""}
          // style={!cardHover ? { pointerEvents: "none" } : undefined}
        >
          {cardHover}
        </div>
      </div>
    </div>
  );
};

export default CardContainer;
